/**
 * The Press Desk — editorial election microsite (election.malaymail.com).
 * Command-centre layout: dominant map + live result panel. Party color = meaning.
 * Margin bars are the recurring data motif; section bands & hairlines over SaaS cards.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Masthead from "@/components/Masthead";
import ConstituencyMap from "@/components/ConstituencyMap";
import SeatPanel from "@/components/SeatPanel";
import { GptAdSlot } from "@/components/AdPortal";
import {
  JOHOR,
  tallyDun,
} from "@/lib/data";
import { party } from "@/lib/parties";
import { Search, ChevronRight, MapPin } from "lucide-react";
import { useLive } from "@/contexts/LiveDataContext";
import LiveLeaderBar from "@/components/LiveLeaderBar";
import LiveFeed from "@/components/LiveFeed";

type Level = "state" | "parliament";

export default function Home() {
  const LIVE = useLive();
  const ds = JOHOR;
  const [mapYear, setMapYear] = useState<"2026" | "2022">("2026");
  const [level, setLevel] = useState<Level>("state");
  const [selected, setSelected] = useState<string | null>(
    // On mobile, start with no seat selected to avoid the overlay appearing immediately
    typeof window !== "undefined" && window.innerWidth < 1024 ? null : "N12"
  );
  const [query, setQuery] = useState("");
  const [partyFilter, setPartyFilter] = useState<string | null>(null);



  // live status map (only meaningful when showing 2026 results)
  const liveMap = useMemo(() => {
    if (mapYear !== "2026") return undefined;
    if (LIVE.level !== level) return undefined;
    const m: Record<string, { status: "leading" | "won" | "pending"; party: string; official?: boolean; majority?: number }> = {};
    Object.entries(LIVE.seatStatus).forEach(([code, s]) => {
      const displayStatus = (s.status === "won_unofficial" || s.status === "won_official") ? "won" : s.status;
      m[code] = { status: displayStatus, party: s.party, official: s.status === "won_official", majority: s.majority };
    });
    return m;
  }, [mapYear, level, LIVE.seatStatus, LIVE.level]);

  const geoUrl = level === "state" ? ds.geo.dun : ds.geo.parliament;

  const resultsMap = useMemo(() => {
    const m: Record<string, any> = {};
    const src =
      level === "state"
        ? ds.dun.map((d) => ({ ...d }))
        : ds.parliament.map((d) => ({ ...d }));
    src.forEach((d: any) => {
      m[d.code] = {
        code: d.code,
        name: d.name,
        party: partyFilter && d.party !== partyFilter ? "OTHER_DIM" : d.party,
        winner: d.winner,
        majority: d.majority,
      };
    });
    return m;
  }, [ds, level, partyFilter]);

  const dunTally = useMemo(() => tallyDun(ds), [ds]);
  const tally = dunTally;

  const allSeats = useMemo(() => {
    return (level === "state" ? ds.dun : ds.parliament).map((d) => ({
      code: d.code,
      name: d.name,
      winner: d.winner,
      party: d.party,
    }));
  }, [ds, level]);

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return allSeats
      .filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q) ||
          s.winner.toLowerCase().includes(q) ||
          (level === "state" &&
            (LIVE.candidatesByContest[s.code] ?? []).some((c) =>
              c.name.toLowerCase().includes(q),
            )),
      )
      .slice(0, 8);
  }, [query, level, allSeats, LIVE.candidatesByContest]);

  const switchLevel = (l: Level) => {
    setLevel(l);
    setSelected(l === "state" ? ds.dun[11]?.code ?? ds.dun[0].code : ds.parliament[0].code);
  };

  /** The shared command centre content (map + rail + panel) */
  /** Desktop command centre: map + seat panel side by side, find-a-seat below */
  const desktopCommandCentre = (
    <div className="bg-card">
      {/* Top row: Map + Seat Panel side by side — responsive height */}
      <div className="grid grid-cols-[minmax(0,1fr)_380px] items-stretch divide-x divide-border min-h-[434px] h-[min(60vh,600px)] overflow-hidden">
        {/* MAP CANVAS */}
        <section className="flex flex-col paper-grain overflow-hidden">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <div>
              <h2 className="font-serif-ed text-xl font-black leading-tight uppercase tracking-wide">LIVE MAP</h2>
              <p className="mt-0.5 text-[10px] text-muted-foreground">Hover or click a seat for details</p>
            </div>
            <div className="flex items-center gap-1 rounded-md border border-border bg-muted p-0.5">
              <button
                onClick={() => setMapYear("2026")}
                className={`rounded px-2.5 py-1 text-xs font-bold transition-colors ${
                  mapYear === "2026"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                2026
              </button>
              <button
                onClick={() => setMapYear("2022")}
                className={`rounded px-2.5 py-1 text-xs font-bold transition-colors ${
                  mapYear === "2022"
                    ? "bg-foreground text-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                2022
              </button>
            </div>
          </div>
          {/* Highlight party */}
          <div className="flex flex-wrap gap-1 border-b border-border px-4 py-1.5">
            <button
              onClick={() => { setPartyFilter(null); setSelected(null); }}
              className={`px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                !partyFilter ? "bg-foreground text-background" : "border border-border"
              }`}
            >
              All
            </button>
            {["BN", "PH", "PN", "MUDA", "BERSAMA"].map((p) => (
              <button
                key={p}
                onClick={() => { setPartyFilter(partyFilter === p ? null : p); setSelected(null); }}
                className="px-2 py-0.5 text-[11px] font-bold transition-opacity"
                style={{
                  background: party(p).color,
                  color: party(p).text,
                  opacity: partyFilter && partyFilter !== p && partyFilter !== "OTHERS" ? 0.35 : 1,
                }}
              >
                {party(p).name}
              </button>
            ))}
            <button
              onClick={() => { setPartyFilter(partyFilter === "OTHERS" ? null : "OTHERS"); setSelected(null); }}
              className={`px-2 py-0.5 text-[11px] font-bold transition-opacity ${partyFilter === "OTHERS" ? "bg-foreground text-background" : "border border-border"}`}
              style={{
                opacity: partyFilter && partyFilter !== "OTHERS" ? 0.35 : 1,
              }}
            >
              Others
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center p-2 overflow-hidden min-h-0">
            <ConstituencyMap
              geoUrl={geoUrl}
              results={resultsMap}
              selectedCode={selected ?? undefined}
              onSelect={setSelected}
              live={liveMap}
              partyFilter={partyFilter}
            />
          </div>
          {mapYear === "2026" && liveMap ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border px-4 py-2">
              <span className="kicker text-muted-foreground">Status</span>
              <span className="flex items-center gap-1.5 text-xs">
                <span className="inline-block h-3 w-3 rounded-sm bg-foreground" /> Declared
              </span>
              <span className="flex items-center gap-1.5 text-xs">
                <span className="inline-block h-3 w-3 rounded-sm overflow-hidden" style={{ background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.6) 2px, rgba(0,0,0,0.6) 4px)', backgroundColor: 'rgba(0,0,0,0.15)' }} /> Leading
              </span>
              <span className="flex items-center gap-1.5 text-xs">
                <span className="inline-block h-3 w-3 rounded-sm border border-border bg-muted" /> Pending
              </span>
              <span className="ml-auto text-[11px] text-muted-foreground">Colour = party</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-4 py-2.5">
              <span className="kicker text-muted-foreground">Won by</span>
              {tally.map((t) => (
                <span key={t.party} className="flex items-center gap-1.5 text-xs">
                  <span
                    className="inline-block h-3 w-3 rounded-sm"
                    style={{ background: party(t.party).color }}
                  />
                  {party(t.party).full}
                  <b className="tnum">{t.seats}</b>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* SEAT PANEL */}
        <aside className="overflow-y-auto overflow-x-hidden bg-card">
          <SeatPanel
            ds={ds}
            level={level}
            code={selected}
            onClose={() => setSelected(null)}
            onSelectDun={(c) => {
              setLevel("state");
              setSelected(c);
            }}
          />
        </aside>
      </div>

      {/* Bottom: Find a Seat */}
      <div className="border-t border-border">
        <div className="p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-foreground">Find a Seat</h3>
            <span className="text-[11px] text-muted-foreground italic">Click any seat for detailed live results</span>
          </div>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search constituency, code or candidate"
              className="w-full border border-border bg-background py-2 pl-8 pr-2 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="grid grid-cols-4 gap-x-2 gap-y-0.5 h-[210px] overflow-y-auto">
            {(query.trim() ? searchResults : allSeats).map((s) => (
              <button
                key={s.code}
                onClick={() => setSelected(s.code)}
                className={`flex items-center justify-between px-2 py-1.5 text-left text-sm transition-colors hover:bg-secondary ${
                  selected === s.code ? "bg-secondary/70" : ""
                }`}
              >
                <span className="truncate font-medium">
                  {s.code} {s.name}
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const commandCentre = (
    <div className="grid grid-cols-1 gap-0 bg-card lg:grid-cols-[230px_minmax(0,1fr)_380px] lg:divide-x lg:divide-border">
      {/* CONTROL RAIL — on mobile: appears after map via order */}
      <aside className="order-2 divide-y divide-border lg:order-none lg:flex lg:flex-col lg:max-h-[86vh]">
        <RailBlock label="Find a seat">
          <p className="text-[11px] text-muted-foreground mb-2 italic">Click any seat for detailed live results</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search constituency, code or candidate"
              className="w-full border border-border bg-background py-2 pl-8 pr-2 text-sm outline-none focus:border-primary"
            />
          </div>
        </RailBlock>

        {/* Full seat listing — scrollable, fills remaining rail height on desktop */}
        <div className="h-[300px] overflow-y-auto lg:flex-1 lg:h-auto lg:min-h-0">
          <div className="space-y-0.5 p-3">
            {(query.trim() ? searchResults : allSeats).map((s) => (
              <button
                key={s.code}
                onClick={() => {
                  setSelected(s.code);
                }}
                className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-sm transition-colors hover:bg-secondary ${
                  selected === s.code ? "bg-secondary/70" : ""
                }`}
              >
                <span className="truncate font-medium">
                  {s.code} {s.name}
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* MAP CANVAS — the lead element; on mobile: appears first */}
      <section className="order-1 flex flex-col bg-card paper-grain lg:order-none">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="font-serif-ed text-2xl font-black leading-tight uppercase tracking-wide">
              LIVE MAP
            </h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Hover or click a seat for details</p>
          </div>
          <div className="flex items-center gap-1 rounded-md border border-border bg-muted p-0.5">
            <button
              onClick={() => setMapYear("2026")}
              className={`rounded px-2.5 py-1 text-xs font-bold transition-colors ${
                mapYear === "2026"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              2026
            </button>
            <button
              onClick={() => setMapYear("2022")}
              className={`rounded px-2.5 py-1 text-xs font-bold transition-colors ${
                mapYear === "2022"
                  ? "bg-foreground text-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              2022
            </button>
          </div>
        </div>
        {/* Highlight party — shown between heading and map on all screens */}
        <div className="flex flex-wrap justify-center gap-1.5 border-b border-border px-3 py-2">
          <button
            onClick={() => { setPartyFilter(null); setSelected(null); }}
            className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors ${
              !partyFilter ? "bg-foreground text-background" : "border border-border"
            }`}
          >
            All
          </button>
          {["BN", "PH", "PN", "MUDA", "BERSAMA"].map((p) => (
            <button
              key={p}
              onClick={() => { setPartyFilter(partyFilter === p ? null : p); setSelected(null); }}
              className="rounded-sm px-2.5 py-1 text-[11px] font-bold transition-opacity"
              style={{
                background: party(p).color,
                color: party(p).text,
                opacity: partyFilter && partyFilter !== p && partyFilter !== "OTHERS" ? 0.35 : 1,
              }}
            >
              {party(p).name}
            </button>
          ))}
          <button
            onClick={() => { setPartyFilter(partyFilter === "OTHERS" ? null : "OTHERS"); setSelected(null); }}
            className={`rounded-sm px-2.5 py-1 text-[11px] font-bold transition-opacity ${partyFilter === "OTHERS" ? "bg-foreground text-background" : "border border-border"}`}
            style={{
              opacity: partyFilter && partyFilter !== "OTHERS" ? 0.35 : 1,
            }}
          >
            Others
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center p-4">
          <ConstituencyMap
            geoUrl={geoUrl}
            results={resultsMap}
            selectedCode={selected ?? undefined}
            onSelect={setSelected}
            live={liveMap}
            partyFilter={partyFilter}
          />
        </div>
        {mapYear === "2026" && liveMap ? (
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t border-border px-3 py-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Status</span>
            <span className="flex items-center gap-1.5 text-[11px]">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-foreground" /> Declared
            </span>
            <span className="flex items-center gap-1.5 text-[11px]">
              <span className="inline-block h-2.5 w-2.5 rounded-sm overflow-hidden" style={{ background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.6) 2px, rgba(0,0,0,0.6) 4px)', backgroundColor: 'rgba(0,0,0,0.15)' }} /> Leading
            </span>
            <span className="flex items-center gap-1.5 text-[11px]">
              <span className="inline-block h-2.5 w-2.5 rounded-sm border border-border bg-muted" /> Pending
            </span>
            <span className="text-[10px] text-muted-foreground">Colour = party</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-4 py-2.5">
            <span className="kicker text-muted-foreground">Won by</span>
            {tally.map((t) => (
              <span key={t.party} className="flex items-center gap-1.5 text-xs">
                <span
                  className="inline-block h-3 w-3 rounded-sm"
                  style={{ background: party(t.party).color }}
                />
                {party(t.party).full}
                <b className="tnum">{t.seats}</b>
              </span>
            ))}
          </div>
        )}
      </section>

      {/* LIVE RESULT PANEL — desktop: inline column; mobile: bottom sheet overlay */}
      {/* Desktop panel (hidden on mobile) */}
      <aside className="hidden border-t border-border bg-card lg:block lg:border-t-0 lg:max-h-[86vh] lg:overflow-y-auto">
        <SeatPanel
          ds={ds}
          level={level}
          code={selected}
          onClose={() => setSelected(null)}
          onSelectDun={(c) => {
            setLevel("state");
            setSelected(c);
          }}
        />
      </aside>
    </div>
  );

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Mobile: clean black bar header */}
      <div className="lg:hidden">
        <header className="relative bg-white border-b border-neutral-200">
          <div className="h-[3px] w-full bg-gradient-to-r from-[#e2001a] via-[#e2001a] to-[#b8001a]" />
          <div className="container flex items-center justify-between py-2">
            {/* Left: Logo + stacked text */}
            <div className="flex items-center gap-2">
              <a href="https://www.malaymail.com" className="transition-opacity hover:opacity-80">
                <img
                  src="/manus-storage/malaymail-mm-logo-cropped_51e33a60.png"
                  alt="Malay Mail"
                  className="h-6 w-auto"
                />
              </a>
              <div className="flex flex-col items-start">
                <span className="text-[11px] font-extrabold tracking-wide uppercase text-neutral-900 leading-tight">
                  16<sup className="text-[0.6em]">th</sup> Johor State Election
                </span>
                <span className="text-[10px] font-medium tracking-wide text-neutral-500 leading-tight">
                  11 July 2026
                </span>
              </div>
            </div>
            {/* Right: Social icons */}
            <div className="flex items-center gap-2">
              <a href="https://www.facebook.com/malaymaildotcom" target="_blank" rel="noopener" className="text-neutral-500 hover:text-neutral-800 transition-colors">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/themalaymail/" target="_blank" rel="noopener" className="text-neutral-500 hover:text-neutral-800 transition-colors">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://x.com/malaymail" target="_blank" rel="noopener" className="text-neutral-500 hover:text-neutral-800 transition-colors">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@themalaymail" target="_blank" rel="noopener" className="text-neutral-500 hover:text-neutral-800 transition-colors">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.88 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.82.11V9.4a6.33 6.33 0 00-.82-.05A6.34 6.34 0 003.15 15.7a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.41a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.84z"/></svg>
              </a>
            </div>
          </div>
        </header>
      </div>

      {/* ========== DESKTOP LAYOUT ========== */}
      <div className="hidden lg:block">
        {/* DESKTOP HEADER: Malay Mail branding + election title — white, sleek */}
        <header className="relative bg-white border-b border-neutral-200">
          <div className="h-[3px] w-full bg-gradient-to-r from-[#e2001a] via-[#e2001a] to-[#b8001a]" />
          <div className="container flex items-center justify-between py-4">
            {/* Left: Logo + Title */}
            <a href="https://www.malaymail.com" className="flex items-center gap-4 group">
              <img
                src="/manus-storage/malaymail-mm-logo-v2_f4e1c23a.png"
                alt="Malay Mail"
                className="h-[4.2rem] w-auto shrink-0 transition-transform group-hover:scale-[1.02]"
              />
              <span className="h-8 w-px bg-neutral-200" />
              <div>
                <h1 className="text-[1.35rem] font-extrabold leading-tight tracking-[-0.01em] text-neutral-900">
                  16<sup className="align-super text-[0.5em] font-bold">th</sup> Johor State Election
                </h1>
                <p className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.1em] text-neutral-500">
                  Polling Day &middot; 11 July 2026
                </p>
              </div>
            </a>

            {/* Right: Social icons + malaymail.com */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2.5">
                <a href="https://www.facebook.com/malaymaildotcom" target="_blank" rel="noopener" className="text-neutral-400 hover:text-neutral-700 transition-colors">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.instagram.com/themalaymail/" target="_blank" rel="noopener" className="text-neutral-400 hover:text-neutral-700 transition-colors">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://x.com/malaymail" target="_blank" rel="noopener" className="text-neutral-400 hover:text-neutral-700 transition-colors">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://www.tiktok.com/@themalaymail" target="_blank" rel="noopener" className="text-neutral-400 hover:text-neutral-700 transition-colors">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.88 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.82.11V9.4a6.33 6.33 0 00-.82-.05A6.34 6.34 0 003.15 15.7a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.41a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.84z"/></svg>
                </a>
              </div>
              <a
                href="https://www.malaymail.com"
                className="flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-600 transition-all hover:border-neutral-300 hover:bg-neutral-100 hover:text-neutral-900"
              >
                <span>malaymail.com</span>
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
              </a>
            </div>
          </div>
        </header>

        {/* MAIN SECTION — content centered, ad rails positioned on sides */}
        <div className="relative mx-auto max-w-[900px] px-4">
          {/* Left 300x600 sticky ad — positioned outside content on desktop */}
          <div className="absolute right-full top-0 mr-4 w-[300px] max-lg:pointer-events-none">
            <div className="sticky top-4 pt-4">
              <GptAdSlot slotId="mm-election-left-300x600" style={{ minWidth: 300, minHeight: 600 }} />
            </div>
          </div>

          {/* Main content column */}
          <div className="w-full">
            {/* Billboard above Latest Updates */}
            <div className="flex justify-center py-4">
              <GptAdSlot slotId="mm-election-billboard" style={{ minWidth: 728, minHeight: 90 }} />
            </div>

            {/* LIVE: LATEST UPDATES */}
            <div className="border-b border-border">
              <div className="py-4">
                <LiveFeed onJumpToMap={() => document.getElementById("election-map")?.scrollIntoView({ behavior: "smooth" })} />
              </div>
            </div>

            {/* LEADER BAR + COMMAND CENTRE — merged into one module */}
            <div id="election-map" className="py-5">
              <div className="border border-foreground">
                {/* Leader bar at top of module */}
                <div className="px-4 py-2 border-b border-foreground">
                  <LiveLeaderBar />
                </div>
                {/* Map + seat panel below */}
                {desktopCommandCentre}
              </div>
            </div>
          </div>

          {/* Right 300x600 sticky ad — positioned outside content on desktop */}
          <div className="absolute left-full top-0 ml-4 w-[300px] max-lg:pointer-events-none">
            <div className="sticky top-4 pt-4">
              <GptAdSlot slotId="mm-election-right-300x600" style={{ minWidth: 300, minHeight: 600 }} />
            </div>
          </div>
        </div>
      </div>

      {/* ========== MOBILE LAYOUT ========== */}
      <div className="lg:hidden">

        {/* MOBILE AD: Billboard below masthead — serves 300x250 or 320x100 */}
        <div className="flex justify-center py-3">
          <GptAdSlot slotId="mm-election-billboard-mobile" />
        </div>

        {/* CLICK HERE FOR LIVE MAP & RESULTS — permanent button */}
        <div className="container py-2">
          <button
            onClick={() => document.getElementById('election-map-mobile')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-primary-foreground shadow-sm active:scale-[0.97] transition-transform"
          >
            <MapPin className="h-3.5 w-3.5" />
            Click here for live map & results ↓
          </button>
        </div>

        {/* LIVE: LATEST UPDATES */}
        <div className="border-b border-border">
          <div className="container py-4">
            <LiveFeed />
          </div>
        </div>

        {/* MOBILE AD: Between Latest Updates and Leader Bar — serves 300x250 or 320x100 */}
        <div className="flex justify-center py-3">
          <GptAdSlot slotId="mm-election-left-300x600-mobile" />
        </div>

        {/* LEADER BAR + COMMAND CENTRE — merged into one module (same as desktop) */}
        <div id="election-map-mobile" className="container py-5">
          <div className="border border-foreground">
            {/* Leader bar at top of module */}
            <div className="px-4 py-3 border-b border-foreground">
              <LiveLeaderBar />
            </div>
            {/* Map + find-a-seat below */}
            {commandCentre}
          </div>
        </div>
      </div>

      {/* Mobile seat panel — bottom sheet overlay */}
      {selected && (
        <div className="fixed inset-0 z-50 flex flex-col lg:hidden">
          {/* Backdrop — tap to dismiss */}
          <div
            className="flex-1 bg-black/40"
            onClick={() => setSelected(null)}
          />
          {/* Panel */}
          <div className="max-h-[70vh] overflow-y-auto border-t-2 border-foreground bg-card shadow-2xl">
            <SeatPanel
              ds={ds}
              level={level}
              code={selected}
              onClose={() => setSelected(null)}
              onSelectDun={(c) => {
                setLevel("state");
                setSelected(c);
              }}
            />
          </div>
        </div>
      )}



      <footer className="border-t border-neutral-200 bg-white">
        <div className="container flex flex-col gap-3 py-6 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-neutral-500 text-xs">
              Data: Election Commission of Malaysia (SPR)
            </p>

            <div className="mt-2 flex items-center gap-3 text-xs">
              <a
                href="https://www.malaymail.com/policy"
                className="uppercase tracking-wider text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-neutral-300">·</span>
              <a
                href="https://www.malaymail.com/terms"
                className="uppercase tracking-wider text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Terms of Use
              </a>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <a
              href="https://www.malaymail.com"
              className="opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/manus-storage/malaymail-mm-logo-v2_f4e1c23a.png"
                alt="Malay Mail"
                className="h-5 w-auto"
              />
            </a>
            <span className="h-4 w-px bg-neutral-200" />
            <a
              href="https://www.mediaworks.com.my"
              className="opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/manus-storage/mediaworks-logo_c83cf0e9.png"
                alt="MediaWorks"
                className="h-3 w-auto"
              />
            </a>
          </div>
        </div>
      </footer>

      {/* 1x1 tracking pixel — invisible */}
      <GptAdSlot slotId="mm-non-balloon" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} />
    </div>
  );
}


function RailBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="p-3">
      <div className="kicker mb-2 text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

/** Floating button (mobile only) that scrolls to the election map section */
function ScrollToMapFab() {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const target = document.getElementById("election-map") || document.getElementById("election-map-mobile");
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Hide when map section is in view
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0.15 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <button
      onClick={() => {
        const el = document.getElementById("election-map") || document.getElementById("election-map-mobile");
        el?.scrollIntoView({ behavior: "smooth" });
      }}
      className={`fixed top-1/2 right-3 z-40 lg:hidden flex h-11 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-bold uppercase tracking-wide text-primary-foreground shadow-lg transition-all duration-300 active:scale-95 ${
        visible ? "opacity-100 -translate-y-1/2" : "opacity-0 -translate-y-1/2 translate-x-4 pointer-events-none"
      }`}
      aria-label="Jump to election map"
    >
      <MapPin className="h-4 w-4" />
      LIVE MAP & RESULTS
    </button>
  );
}


